import React, { createContext, useContext, useState, useEffect } from 'react';

interface Theme {
  background: string;
  surface: string;
  text: string;
  textSecondary: string;
  border: string;
  primary: string;
  primaryHover: string;
  secondary: string;
  success: string;
  warning: string;
  danger: string;
  sidebar: string;
}

interface ThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
  theme: Theme;
}

const lightTheme: Theme = {
  background: '#ffffff',
  surface: '#f8f9fa',
  text: '#212529',
  textSecondary: '#6c757d',
  border: '#dee2e6',
  primary: '#ff6b35',      // Orange
  primaryHover: '#e55a2b',  // Darker orange
  secondary: '#fd7e14',     // Orange variant
  success: '#28a745',
  warning: '#ffc107',
  danger: '#dc3545',
  sidebar: '#fff5f0'        // Light orange tint
};

const darkTheme: Theme = {
  background: '#1a1a1a',
  surface: '#2d2d2d',
  text: '#ffffff',
  textSecondary: '#adb5bd',
  border: '#495057',
  primary: '#ff6b35',      // Orange (same)
  primaryHover: '#ff8c5a',  // Lighter orange for dark mode
  secondary: '#fd7e14',     // Orange variant
  success: '#28a745',
  warning: '#ffc107',
  danger: '#dc3545',
  sidebar: '#2a1f1a'        // Dark orange tint
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('wallboard-theme');
    if (saved) {
      setIsDark(saved === 'dark');
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    localStorage.setItem('wallboard-theme', newTheme ? 'dark' : 'light');
  };

  const theme = isDark ? darkTheme : lightTheme;

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme, theme }}>
      <div style={{ 
        backgroundColor: theme.background, 
        color: theme.text, 
        minHeight: '100vh',
        transition: 'background-color 0.3s ease, color 0.3s ease'
      }}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};